import { useCallback, useEffect, useMemo, useState } from 'react';

import dayjs from 'dayjs';

import { InviteItemFilters } from '../../../api/types';
import {
  FilterOption,
  FilterSection,
  InviteItemReason,
  InviteItemResourceType,
  InviteItemStatus,
  InviteStatus,
} from '../../../types';
import {
  INVITE_ITEM_REASON_LABELS,
  INVITE_ITEM_STATUS_LABELS,
  INVITE_RESOURCE_TYPE_LABELS,
  INVITE_STATUS_LABELS,
} from '../config';
import { InviteFilterInviter, InviteFilterResource, InviteProps } from '../types';

// The autocomplete shows `name`; a team and a location may share one, so the type rides along
interface ResourceOption {
  name: string;
  uuid: string;
  type: InviteItemResourceType;
}

interface InviterOption {
  name: string;
  uuid: string;
}

interface FilterValues {
  statuses?: string[];
  item_statuses?: string[];
  reasons?: string[];
  resource_type?: string;
  resource?: ResourceOption | null;
  invited_by?: InviterOption | null;
  invited_from?: string | null;
  invited_to?: string | null;
}

export interface InviteRequestFilters extends InviteItemFilters {
  statuses?: string[];
}

// The select works on labels — map them back to the API values
const invert = <K extends string>(labels: Partial<Record<K, string>>) =>
  Object.fromEntries(Object.entries(labels).map(([key, label]) => [label, key])) as Record<string, K>;

const STATUS_BY_LABEL = invert<InviteStatus>(INVITE_STATUS_LABELS);
const ITEM_STATUS_BY_LABEL = invert<InviteItemStatus>(INVITE_ITEM_STATUS_LABELS);
const REASON_BY_LABEL = invert<InviteItemReason>(INVITE_ITEM_REASON_LABELS);
const RESOURCE_TYPE_BY_LABEL = invert<InviteItemResourceType>(INVITE_RESOURCE_TYPE_LABELS);

const fromLabels = <K extends string>(labels: string[] | undefined, map: Record<string, K>): K[] =>
  (labels ?? []).map(label => map[label]).filter(Boolean);

/** Turns what the filter drawer holds into the search body. Empty fields are left out. */
export const toInviteRequestFilters = (values: FilterValues): InviteRequestFilters => {
  const filters: InviteRequestFilters = {};

  const statuses = fromLabels(values.statuses, STATUS_BY_LABEL);
  if (statuses.length) filters.statuses = statuses;

  const itemStatuses = fromLabels(values.item_statuses, ITEM_STATUS_BY_LABEL);
  if (itemStatuses.length) filters.item_statuses = itemStatuses;

  const reasons = fromLabels(values.reasons, REASON_BY_LABEL);
  if (reasons.length) filters.reasons = reasons;

  // A picked place pins its own type; otherwise the type select narrows on its own
  if (values.resource?.uuid) {
    filters.resource_id = values.resource.uuid;
    filters.resource_type = values.resource.type;
  } else if (values.resource_type && RESOURCE_TYPE_BY_LABEL[values.resource_type]) {
    filters.resource_type = RESOURCE_TYPE_BY_LABEL[values.resource_type];
  }

  if (values.invited_by?.uuid) filters.invited_by = values.invited_by.uuid;

  // Whole local days, sent as UTC instants — both ends are inclusive on the server
  if (values.invited_from) filters.invited_from = dayjs(values.invited_from).startOf('day').toISOString();
  if (values.invited_to) filters.invited_to = dayjs(values.invited_to).endOf('day').toISOString();

  return filters;
};

const multiSelect = (label: string, field: string, values: string[]): FilterOption => ({
  type: 'select',
  multiple: true,
  label,
  field,
  values,
  defaultValue: [],
});

interface Params
  extends Pick<InviteProps, 'selectedCompanyId' | 'fetchFilterResources' | 'fetchFilterInviters'> {
  /** The page fixes the invite statuses itself (Pending invites) — don't offer them again */
  hasFixedStatuses: boolean;
}

export const useInviteFilters = ({
  selectedCompanyId,
  fetchFilterResources,
  fetchFilterInviters,
  hasFixedStatuses,
}: Params) => {
  const [filters, setFilters] = useState<InviteRequestFilters>({});
  const [resources, setResources] = useState<InviteFilterResource[]>([]);
  const [inviters, setInviters] = useState<InviteFilterInviter[]>([]);

  useEffect(() => {
    if (!selectedCompanyId || !fetchFilterResources) {
      setResources([]);
      return;
    }
    let cancelled = false;
    fetchFilterResources(selectedCompanyId)
      .then(items => !cancelled && setResources(items))
      .catch(e => console.error(e));
    return () => {
      cancelled = true;
    };
  }, [selectedCompanyId, fetchFilterResources]);

  useEffect(() => {
    if (!selectedCompanyId || !fetchFilterInviters) {
      setInviters([]);
      return;
    }
    let cancelled = false;
    fetchFilterInviters(selectedCompanyId)
      .then(items => !cancelled && setInviters(items))
      // Listing staff takes its own right; without it the "Invited by" filter just isn't offered
      .catch(e => console.error(e));
    return () => {
      cancelled = true;
    };
  }, [selectedCompanyId, fetchFilterInviters]);

  const filtersSections = useMemo<FilterSection[]>(() => {
    const placeFilters: FilterOption[] = [
      multiSelect('Place status', 'item_statuses', Object.values(INVITE_ITEM_STATUS_LABELS)),
      multiSelect('Reason', 'reasons', Object.values(INVITE_ITEM_REASON_LABELS) as string[]),
      {
        type: 'select',
        multiple: false,
        label: 'Place type',
        field: 'resource_type',
        values: ['', ...Object.values(INVITE_RESOURCE_TYPE_LABELS)],
        defaultValue: '',
      },
    ];

    if (resources.length) {
      const options: ResourceOption[] = resources.map(resource => ({
        name: `${resource.name} · ${INVITE_RESOURCE_TYPE_LABELS[resource.type]}`,
        uuid: resource.uuid,
        type: resource.type,
      }));
      placeFilters.push({
        type: 'autocomplete',
        multiple: false,
        label: 'Place',
        field: 'resource',
        values: options,
        defaultValue: null,
      });
    }

    const invitedFilters: FilterOption[] = [];
    if (inviters.length) {
      invitedFilters.push({
        type: 'autocomplete',
        multiple: false,
        label: 'Invited by',
        field: 'invited_by',
        values: inviters,
        defaultValue: null,
      });
    }
    invitedFilters.push(
      { type: 'date', label: 'Invited from', field: 'invited_from', defaultValue: null },
      { type: 'date', label: 'Invited to', field: 'invited_to', defaultValue: null }
    );

    const sections: FilterSection[] = [];
    if (!hasFixedStatuses) {
      sections.push({
        title: 'Invite',
        expanded: true,
        filters: [multiSelect('Status', 'statuses', Object.values(INVITE_STATUS_LABELS))],
      });
    }
    // All place filters must match the same place — say so where they are set
    sections.push({ title: 'Places (all must match one place)', expanded: true, filters: placeFilters });
    sections.push({ title: 'Invited', expanded: false, filters: invitedFilters });
    return sections;
  }, [hasFixedStatuses, resources, inviters]);

  const handleFilterChange = useCallback((values: FilterValues) => {
    setFilters(toInviteRequestFilters(values));
  }, []);

  return { filters, filtersSections, handleFilterChange };
};
